import { createTheme } from '@mui/material/styles';

// Extend MUI's palette types to include our custom categoryItem tokens
declare module '@mui/material/styles' {
    interface Palette {
        categoryItem: {
            default: string;
            self: string;
            highlighted: string;
            edit: string;
            empty: string;
        };
    }
    interface PaletteOptions {
        categoryItem?: {
            default?: string;
            self?: string;
            highlighted?: string;
            edit?: string;
            empty?: string;
        };
    }
}

// CSS variable references — these resolve to the active color scheme's values automatically
export const categoryItemVars = {
    default: 'var(--mui-palette-categoryItem-default)',
    self: 'var(--mui-palette-categoryItem-self)',
    highlighted: 'var(--mui-palette-categoryItem-highlighted)',
    edit: 'var(--mui-palette-categoryItem-edit)',
    empty: 'var(--mui-palette-categoryItem-empty)',
} as const;

// Reusable style constants for consistent design across components
export const themeConstants = {
    gradients: {
        // Keep only essential background gradients
        primary: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        secondary: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
        error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        footer: 'linear-gradient(to top, rgba(30,41,59,0.8) 0%, transparent 100%)',

        // Card gradients with transparency - kept for subtle backgrounds
        cardPrimary: 'linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.1) 100%)',
        cardSuccess: 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.1) 100%)',
        cardSecondary:
            'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(124,58,237,0.1) 100%)',
        cardWarning: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(217,119,6,0.1) 100%)',
        cardError: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(220,38,38,0.1) 100%)',
    },
    borders: {
        primary: '1px solid rgba(59,130,246,0.2)',
        primaryStrong: '1px solid rgba(59,130,246,0.3)',
        primaryHover: '1px solid rgba(59,130,246,0.5)',
        success: '1px solid rgba(16,185,129,0.2)',
        secondary: '1px solid rgba(139,92,246,0.2)',
        warning: '1px solid rgba(245,158,11,0.2)',
        error: '1px solid rgba(239,68,68,0.2)',
        divider: '1px solid rgba(51,65,85,0.5)',
        subtle: '1px solid rgba(51,65,85,0.1)',
    },
    backgrounds: {
        glass: 'rgba(30,41,59,0.5)',
        glassHover: 'rgba(59,130,246,0.1)',
        glassStrong: 'rgba(15,23,42,0.6)',
        iconBox: 'rgba(255,255,255,0.2)',
    },
    effects: {
        backdropBlur: 'blur(10px)',
        boxShadowHover: '0 4px 12px rgba(0,0,0,0.3)',
        boxShadowGlow: '0 0 20px rgba(59,130,246,0.2)',
    },
    transitions: {
        standard: 'all 0.2s ease',
        smooth: 'all 0.3s ease',
    },
    patterns: {
        dots: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
    },
};

// Reusable style helper functions
export const styleHelpers = {
    // Modal/Dialog header with gradient and pattern
    dialogHeader: (gradient: string = themeConstants.gradients.primary) => ({
        background: gradient,
        p: 3,
        position: 'relative' as const,
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: themeConstants.patterns.dots,
            opacity: 0.1,
        },
    }),

    // Icon box for headers
    iconBox: (size: number = 56) => ({
        width: size,
        height: size,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: themeConstants.backgrounds.iconBox,
        backdropFilter: themeConstants.effects.backdropBlur,
    }),

    // Info card with gradient background
    infoCard: (
        gradient: string = themeConstants.gradients.cardPrimary,
        border: string = themeConstants.borders.primary
    ) => ({
        background: gradient,
        border,
        borderRadius: 2,
        transition: themeConstants.transitions.standard,
    }),

    // Chip with solid color background instead of gradient
    gradientChip: (isActive: boolean = false) => ({
        fontWeight: isActive ? 700 : 500,
        background: isActive ? 'rgba(59,130,246,0.2)' : 'transparent',
        borderColor: isActive ? 'primary.main' : 'divider',
        borderWidth: 2,
        backdropFilter: themeConstants.effects.backdropBlur,
        transition: themeConstants.transitions.standard,
        '&:hover': {
            boxShadow: themeConstants.effects.boxShadowHover,
            borderColor: 'primary.light',
        },
    }),

    // Dialog footer with gradient
    dialogFooter: () => ({
        p: 3,
        borderTop: '1px solid',
        borderColor: 'divider',
        background: themeConstants.gradients.footer,
    }),

    // Status badge for user cards
    statusBadge: (isActive: boolean) => ({
        fontSize: 16,
        color: isActive ? 'success.main' : 'error.main',
        bgcolor: 'background.paper',
        borderRadius: '50%',
    }),

    // Avatar with role-based color
    roleAvatar: (isAdmin: boolean, size: number = 56) => ({
        bgcolor: isAdmin ? 'warning.main' : 'primary.main',
        width: size,
        height: size,
        fontWeight: 700,
    }),

    // Preference/Settings option box
    settingsOption: (
        hoverBg: string = themeConstants.backgrounds.glassHover,
        hoverBorder: string = 'primary.main'
    ) => ({
        p: 2,
        borderRadius: 2,
        bgcolor: themeConstants.backgrounds.glass,
        border: themeConstants.borders.divider,
        m: 0,
        width: '100%',
        transition: themeConstants.transitions.standard,
        '&:hover': {
            bgcolor: hoverBg,
            borderColor: hoverBorder,
        },
    }),
};

export const theme = createTheme({
    defaultColorScheme: 'dark',
    cssVariables: {
        colorSchemeSelector: 'class',
    },
    colorSchemes: {
        dark: {
            palette: {
                primary: {
                    main: '#3b82f6',
                    light: '#60a5fa',
                    dark: '#2563eb',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#8b5cf6',
                    light: '#a78bfa',
                    dark: '#7c3aed',
                },
                background: {
                    default: '#0f172a',
                    paper: '#1e293b',
                },
                warning: {
                    main: '#f59e0b',
                    light: '#fbbf24',
                    dark: '#d97706',
                },
                success: {
                    main: '#10b981',
                    light: '#34d399',
                    dark: '#059669',
                },
                error: {
                    main: '#ef4444',
                    light: '#f87171',
                    dark: '#dc2626',
                },
                info: {
                    main: '#06b6d4',
                    light: '#22d3ee',
                    dark: '#0891b2',
                },
                text: {
                    primary: '#f1f5f9',
                    secondary: '#94a3b8',
                },
                divider: '#334155',
                categoryItem: {
                    default: 'rgba(30,41,59,0.5)',
                    self: 'rgba(59,130,246,0.15)',
                    highlighted: 'rgba(245,158,11,0.15)',
                    edit: 'rgba(51,65,85,0.3)',
                    empty: 'rgba(30,41,59,0.3)',
                },
            },
        },
        light: {
            palette: {
                primary: {
                    main: '#2563eb',
                    light: '#3b82f6',
                    dark: '#1d4ed8',
                    contrastText: '#fff',
                },
                secondary: {
                    main: '#7c3aed',
                    light: '#8b5cf6',
                    dark: '#6d28d9',
                    contrastText: '#fff',
                },
                background: {
                    default: '#f1f5f9',
                    paper: '#ffffff',
                },
                warning: {
                    main: '#d97706',
                    light: '#f59e0b',
                    dark: '#b45309',
                },
                success: {
                    main: '#059669',
                    light: '#10b981',
                    dark: '#047857',
                },
                error: {
                    main: '#dc2626',
                    light: '#ef4444',
                    dark: '#b91c1c',
                },
                info: {
                    main: '#0891b2',
                    light: '#06b6d4',
                    dark: '#0e7490',
                },
                text: {
                    primary: '#0f172a',
                    secondary: '#475569',
                },
                divider: '#cbd5e1',
                action: {
                    hover: 'rgba(37,99,235,0.08)',
                    selected: 'rgba(37,99,235,0.12)',
                },
                categoryItem: {
                    default: 'rgba(241,245,249,0.8)',
                    self: 'rgba(37,99,235,0.08)',
                    highlighted: 'rgba(217,119,6,0.10)',
                    edit: 'rgba(226,232,240,0.6)',
                    empty: 'rgba(226,232,240,0.4)',
                },
            },
        },
    },
    typography: {
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        h4: {
            fontWeight: 700,
        },
        h5: {
            fontWeight: 700,
        },
        h6: {
            fontWeight: 600,
        },
        button: {
            textTransform: 'none',
            fontWeight: 600,
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundAttachment: 'fixed',
                    backgroundColor: 'var(--mui-palette-background-default)',
                    color: 'var(--mui-palette-text-primary)',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    minHeight: 36,
                    paddingTop: 6,
                    paddingBottom: 6,
                    paddingLeft: 12,
                    paddingRight: 12,
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
                sizeSmall: {
                    minHeight: 32,
                    paddingTop: 4,
                    paddingBottom: 4,
                    paddingLeft: 8,
                    paddingRight: 8,
                },
            },
            variants: [
                {
                    props: {
                        variant: 'outlined',
                        color: 'inherit',
                    },
                    style: {
                        borderColor: 'var(--mui-palette-divider)',
                        '&:hover': {
                            backgroundColor: 'var(--mui-palette-action-hover)',
                        },
                    },
                },
            ],
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    minWidth: 36,
                    minHeight: 36,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    borderRadius: 12,
                },
                elevation1: {
                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                },
                elevation2: {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
                },
                elevation3: {
                    boxShadow: '0 4px 6px rgba(0,0,0,0.12)',
                },
                elevation4: {
                    boxShadow: '0 6px 12px rgba(0,0,0,0.16)',
                },
                elevation8: {
                    boxShadow: '0 8px 16px rgba(0,0,0,0.16), 0 0 0 1px rgba(59,130,246,0.2)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    border: '1px solid rgba(59,130,246,0.3)',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#3b82f6',
                    paddingBottom: '12px',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                            borderColor: '#3b82f6',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: 2,
                        },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 700,
                    backgroundColor: 'rgba(59,130,246,0.1)',
                    borderBottom: '2px solid rgba(59,130,246,0.3)',
                },
            },
        },
        MuiCard: {
            defaultProps: {
                elevation: 2,
            },
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'all 0.3s ease',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    '&:last-child': {
                        paddingBottom: 16,
                    },
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontWeight: 700,
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 700,
                    fontSize: '0.75rem',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    '&.Mui-checked': {
                        color: '#3b82f6',
                    },
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '24px',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        borderRadius: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(59,130,246,0.5)',
                        borderRadius: '8px',
                        '&:hover': {
                            background: 'rgba(59,130,246,0.7)',
                        },
                    },
                },
            },
        },
        MuiStack: {
            defaultProps: {
                useFlexGap: true,
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '0.875rem',
                    padding: '8px 12px',
                    borderRadius: '8px',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#3b82f6',
                        borderWidth: 2,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    padding: 8,
                },
                track: {
                    borderRadius: 22 / 2,
                },
                thumb: {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                },
                switchBase: {
                    '&.Mui-checked': {
                        color: '#3b82f6',
                        '& + .MuiSwitch-track': {
                            backgroundColor: '#3b82f6',
                            opacity: 0.7,
                        },
                    },
                },
            },
        },
    },
});
